import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getStock } from '../utils/api';
import { ArrowRight, Zap, TrendingUp, Shield } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [stock, setStock] = useState(null);

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async () => {
    try {
      const response = await getStock();
      setStock(response.data);
    } catch (error) {
      console.error('Failed to fetch stock:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1219] relative overflow-hidden">
      {/* Scanline Overlay */}
      <div className="scanline-overlay" />

      {/* Glow Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Navbar */}
      <nav className="relative z-10 border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-2 h-2 bg-primary" data-testid="logo-accent" />
            <h1 className="text-3xl font-heading font-bold tracking-wider text-primary" data-testid="logo-text">GRINDNATION</h1>
          </motion.div>
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/admin/login')}
            className="px-6 py-2 border border-primary/50 text-primary hover:bg-primary/10 font-heading tracking-widest uppercase text-sm"
            data-testid="admin-login-button"
          >
            ADMIN
          </motion.button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 px-4 py-2 border border-primary/30 bg-primary/5">
                <div className="w-2 h-2 bg-primary animate-pulse" />
                <span className="text-primary text-sm font-mono uppercase tracking-wider">Live Stock Available</span>
              </div>
            </div>

            <h1 className="text-5xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
              <span className="text-white">DELIVER</span>
              <br />
              <span className="text-primary">POPULARITY</span>
              <br />
              <span className="text-white">AT SCALE</span>
            </h1>

            <p className="text-gray-400 text-lg mb-8 max-w-lg">
              Professional popularity delivery management system for gaming communities. Fast, reliable, and secure transactions.
            </p>

            {stock && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mb-8 p-6 bg-black/40 backdrop-blur-md border border-primary/20"
                data-testid="stock-display"
              >
                <div className="flex items-baseline gap-3">
                  <span className="text-5xl font-mono font-bold text-primary">{stock.available_k}</span>
                  <span className="text-2xl text-gray-400 font-mono">K</span>
                </div>
                <p className="text-gray-500 text-sm mt-2 font-mono uppercase tracking-wider">Available Stock</p>
              </motion.div>
            )}

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/order')}
              className="group relative px-8 py-4 bg-primary text-black font-heading font-bold text-lg tracking-widest uppercase overflow-hidden"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 90% 100%, 0 100%)' }}
              data-testid="create-order-button"
            >
              <span className="relative z-10 flex items-center gap-2">
                CREATE ORDER
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
            </motion.button>

            {/* Admin Contact Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-8 p-6 bg-black/40 backdrop-blur-md border border-primary/20"
              data-testid="admin-contact-card"
            >
              <h3 className="text-primary font-heading font-bold text-lg tracking-wider mb-4">CONTACT ADMIN</h3>
              <div className="space-y-3">
                <a
                  href="https://t.me/sketchx22"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-300 hover:text-primary transition-colors group"
                  data-testid="telegram-contact"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.155.233.171.326.016.093.036.305.02.469z"/>
                  </svg>
                  <span className="font-mono">@sketchx22</span>
                </a>
                <a
                  href="https://wa.me/918920598379"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-300 hover:text-primary transition-colors group"
                  data-testid="whatsapp-contact"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span className="font-mono">+91 8920598379</span>
                </a>
                <a
                  href="mailto:admin@grindnation.shop"
                  className="flex items-center gap-3 text-gray-300 hover:text-primary transition-colors group"
                  data-testid="email-contact"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="font-mono">admin@grindnation.shop</span>
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-2 gap-4"
          >
            <FeatureCard
              icon={<Zap />}
              title="INSTANT"
              description="Real-time processing"
              delay={0.5}
            />
            <FeatureCard
              icon={<TrendingUp />}
              title="SCALABLE"
              description="High volume support"
              delay={0.6}
            />
            <FeatureCard
              icon={<Shield />}
              title="SECURE"
              description="Encrypted transactions"
              delay={0.7}
            />
            <FeatureCard
              icon={<Zap />}
              title="RELIABLE"
              description="99.9% uptime"
              delay={0.8}
            />
          </motion.div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <StatCard number="24/7" label="Operations" />
          <StatCard number="<1min" label="Processing Time" />
          <StatCard number="100%" label="Satisfaction" />
        </motion.div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{ y: -4 }}
    className="relative p-6 bg-card border border-border/50 group hover:border-primary/50 transition-colors"
    data-testid={`feature-card-${title.toLowerCase()}`}
  >
    <div className="absolute top-0 right-0 w-2 h-2 bg-primary" />
    <div className="text-primary mb-3">{icon}</div>
    <h3 className="text-white font-heading font-bold text-xl tracking-wider mb-2">{title}</h3>
    <p className="text-gray-400 text-sm">{description}</p>
  </motion.div>
);

const StatCard = ({ number, label }) => (
  <div className="p-6 bg-black/40 backdrop-blur-md border border-primary/20" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-4xl font-mono font-bold text-primary mb-2">{number}</div>
    <div className="text-gray-400 text-sm font-mono uppercase tracking-wider">{label}</div>
  </div>
);

export default Landing;